<script setup lang="ts">
/*
 * Routes whose meta.layout === 'auth' render their RouterView bare. Every
 * other route nests inside AppShell. The shell's user menu surfaces the
 * change-password modal at the App level so it floats above the layout
 * regardless of the current route.
 */
import { computed, ref, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import { RouterView, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AppShell from '@/shell/AppShell.vue';
import PaySubNav from '@/shell/PaySubNav.vue';
import ChangePasswordModal from '@/auth/ChangePasswordModal.vue';
import ErrorDialog from '@/shell/ErrorDialog.vue';
import GlobalProgressBar from '@/shell/GlobalProgressBar.vue';
import { useAuthStore } from '@/stores/auth';

// Keep `<html lang>` in sync with the active vue-i18n locale so screen
// readers pick the right voice for Khmer text. (Wave 3.3 a11y.)
const { locale } = useI18n();
watchEffect(() => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale.value;
  }
});

const route = useRoute();
const auth = useAuthStore();
const { claims } = storeToRefs(auth);

const isAuthLayout = computed(() => route.meta['layout'] === 'auth');
const showPaySubNav = computed(() => route.meta['nav'] === 'pay');
const changePasswordOpen = ref(false);

// Routes that carry a path param (e.g. `/people/profile/:id`,
// `/pay/run/:payrollId`) need the param included in the key so the
// page remounts when the id changes; otherwise a same-route id swap
// would silently keep the previous detail in view.
const routeViewKey = computed(() => {
  const name = String(route.name ?? '');
  const id = route.params['id'] ?? route.params['payrollId'] ?? '';
  return id ? `${name}::${id}` : name;
});

const user = computed(() => ({
  name: claims.value?.email?.split('@')[0] ?? 'User',
  email: claims.value?.email ?? '',
}));

async function handleSignOut() {
  await auth.signOut();
  // Hard-navigate to the welcome screen so Amplify's auth state, the
  // Pinia store, every cached query, and any open modal/menu tear down
  // cleanly. `router.push` was leaving the user on the same screen on
  // some intermittent click paths (open user-menu + click sign-out
  // while another animation was settling).
  window.location.assign('/welcome');
}
</script>

<template>
  <!--
    Global in-flight-request progress bar. Mounted ONCE at the root (it
    teleports itself to <body>) so the thin top bar floats above BOTH the
    auth layout (the /welcome sign-in-config lookup) and the app shell —
    TopBar, drawers, and modals included.
  -->
  <GlobalProgressBar />
  <RouterView v-if="isAuthLayout" />
  <AppShell
    v-else
    :user="user"
    @sign-out="handleSignOut"
    @change-password="changePasswordOpen = true"
  >
    <template v-if="showPaySubNav" #subnav>
      <PaySubNav />
    </template>
    <!--
      Key the inner RouterView by route name (and detail-id for routes
      that take one) so module-to-module navigation forces a clean
      remount of the page component instead of Vue trying to patch the
      previous module's tree. The user-reported intermittent "click
      Home from People doesn't take effect" trace pointed at stale
      child state surviving the patch path. Tab/query changes within a
      module still reuse the component (route.name doesn't change).
    -->
    <RouterView :key="routeViewKey" />
  </AppShell>
  <ChangePasswordModal :open="changePasswordOpen" @close="changePasswordOpen = false" />
  <!--
    Global failed-backend-response dialog. Mounted once at the root (NOT
    per-route) so it floats over both the auth layout and the app shell.
    Closing it only clears the store — it never navigates or reloads, so the
    user's current view + any half-filled form survive.
  -->
  <ErrorDialog />
</template>
